import { PayerName } from './types';

// Top payers with comprehensive simulation data
export interface PayerDriftData {
  payer: PayerName;
  driftScore: number;
  isHighVolatility: boolean;
  ruleChangeAlert: string;
  lastUpdated: Date;
  topChange: string;
  impactAmount: number;
  suggestedFix: string;
}

export interface OrthopedicBenchmark {
  cptCode: string;
  description: string;
  beaconCleanClaimRate: number;
  nationalAverage: number;
  variance: number;
  claimVolume: number;
}

export interface PayerAlgorithmStep {
  step: number;
  action: string;
  status: 'probing' | 'success' | 'adjusting' | 'complete';
  detail: string;
  timestamp: Date;
}

export interface UnderpaymentRecord {
  id: string;
  payer: PayerName;
  claimId: string;
  cptCode: string;
  expectedRate: number;
  actualPaid: number;
  variance: number;
  variancePercent: number;
  recoveryStatus: 'detected' | 'auto-appealed' | 'recovered' | 'pending';
  recoveryAmount: number;
}

// Generate live payer drift data with high volatility markers
export function generatePayerDriftRadar(): PayerDriftData[] {
  const payers: PayerName[] = ['UnitedHealthcare', 'BCBS', 'Aetna', 'Cigna', 'Medicare'];
  
  const ruleChangeAlerts: Record<PayerName, string> = {
    'UnitedHealthcare': 'UHC updated modifier -59 requirements for orthopedic codes 24h ago. AI agents already updated.',
    'BCBS': 'BCBS revised prior auth criteria for arthroscopy procedures. Auto-submission rules adjusted.',
    'Aetna': 'Aetna tightened medical necessity documentation for TKA. Templates pre-populated.',
    'Cigna': 'Cigna reduced PT visit limits from 20 to 15. Patient notification queued.',
    'Medicare': 'CMS updated LCD for 27447. Compliance verified across all pending claims.',
    'Humana': 'Humana increased timely filing window to 120 days. No action required.',
  };

  const topChanges: Record<PayerName, string> = {
    'UnitedHealthcare': 'Modifier -59 policy update',
    'BCBS': 'Prior auth threshold reduced',
    'Aetna': 'Documentation requirements increased',
    'Cigna': 'PT visit limits changed',
    'Medicare': 'LCD coverage criteria updated',
    'Humana': 'Filing deadline extended',
  };

  const suggestedFixes: Record<PayerName, string> = {
    'UnitedHealthcare': 'Auto-applying XE modifier for distinct encounters',
    'BCBS': 'Pre-auth bot activated for procedures >$2,500',
    'Aetna': 'Enhanced note templates deployed',
    'Cigna': 'Patient outreach scheduled',
    'Medicare': 'ICD-10 specificity increased',
    'Humana': 'No action required',
  };

  return payers.map((payer) => {
    // Generate drift scores with some variability
    const baseDrift: Record<PayerName, number> = {
      'UnitedHealthcare': 82,
      'BCBS': 45,
      'Aetna': 78,
      'Cigna': 62,
      'Medicare': 38,
      'Humana': 55,
    };
    
    const driftScore = Math.min(100, Math.max(0, baseDrift[payer] + Math.floor(Math.random() * 20) - 10));
    
    return {
      payer,
      driftScore,
      isHighVolatility: driftScore >= 75,
      ruleChangeAlert: ruleChangeAlerts[payer],
      lastUpdated: new Date(Date.now() - Math.random() * 86400000), // Within last 24h
      topChange: topChanges[payer],
      impactAmount: Math.floor(Math.random() * 50000) + 5000,
      suggestedFix: suggestedFixes[payer],
    };
  });
}

// Generate orthopedic-specific benchmarks for Beacon
export function generateOrthopedicBenchmarks(): OrthopedicBenchmark[] {
  return [
    {
      cptCode: '27447',
      description: 'Total Knee Arthroplasty',
      beaconCleanClaimRate: 97.8,
      nationalAverage: 83.6,
      variance: 14.2,
      claimVolume: 247,
    },
    {
      cptCode: '29881',
      description: 'Knee Arthroscopy w/ Meniscectomy',
      beaconCleanClaimRate: 96.4,
      nationalAverage: 84.1,
      variance: 12.3,
      claimVolume: 189,
    },
    {
      cptCode: '27130',
      description: 'Total Hip Arthroplasty',
      beaconCleanClaimRate: 98.1,
      nationalAverage: 85.2,
      variance: 12.9,
      claimVolume: 156,
    },
    {
      cptCode: '29827',
      description: 'Arthroscopic Rotator Cuff Repair',
      beaconCleanClaimRate: 95.7,
      nationalAverage: 82.4,
      variance: 13.3,
      claimVolume: 134,
    },
    {
      cptCode: '20610',
      description: 'Arthrocentesis (Major Joint)',
      beaconCleanClaimRate: 99.2,
      nationalAverage: 91.5,
      variance: 7.7,
      claimVolume: 412,
    },
  ];
}

// Generate payer algorithm probing steps
export function generateAlgorithmSteps(payer: PayerName): PayerAlgorithmStep[] {
  const now = new Date();
  
  const payerSpecificTests: Record<PayerName, PayerAlgorithmStep[]> = {
    'Aetna': [
      { step: 1, action: 'Testing medical necessity thresholds', status: 'complete', detail: 'ICD-10 M17.11 validated against LCD', timestamp: new Date(now.getTime() - 4000) },
      { step: 2, action: 'Probing prior auth requirements', status: 'complete', detail: 'Auth required for CPT 27447 >$15,000', timestamp: new Date(now.getTime() - 3000) },
      { step: 3, action: 'Analyzing bundling rules', status: 'success', detail: 'Modifier -59 accepted for distinct encounters', timestamp: new Date(now.getTime() - 2000) },
      { step: 4, action: 'Optimizing submission window', status: 'adjusting', detail: 'Shifting to Tuesday AM for 12% faster processing', timestamp: new Date(now.getTime() - 1000) },
      { step: 5, action: 'Calibrating reimbursement expectations', status: 'probing', detail: 'Contractual rate $18,450 ± 3.2%', timestamp: now },
    ],
    'UnitedHealthcare': [
      { step: 1, action: 'Validating modifier -59 compliance', status: 'complete', detail: 'New XE/XP/XS/XU modifiers preferred', timestamp: new Date(now.getTime() - 4000) },
      { step: 2, action: 'Testing bundling thresholds', status: 'complete', detail: 'NCCI edits version 29.1 applied', timestamp: new Date(now.getTime() - 3000) },
      { step: 3, action: 'Probing timely filing limits', status: 'success', detail: '90-day window confirmed', timestamp: new Date(now.getTime() - 2000) },
      { step: 4, action: 'Analyzing denial patterns', status: 'adjusting', detail: 'CO-4 frequency reduced 34%', timestamp: new Date(now.getTime() - 1000) },
      { step: 5, action: 'Optimizing cash velocity', status: 'probing', detail: 'Target: 14 days to payment', timestamp: now },
    ],
    'BCBS': [
      { step: 1, action: 'Checking prior auth database', status: 'complete', detail: 'Auth #PA-2026-447821 verified', timestamp: new Date(now.getTime() - 4000) },
      { step: 2, action: 'Testing coverage limits', status: 'complete', detail: 'In-network benefit confirmed', timestamp: new Date(now.getTime() - 3000) },
      { step: 3, action: 'Validating place of service', status: 'success', detail: 'ASC approved for procedure', timestamp: new Date(now.getTime() - 2000) },
      { step: 4, action: 'Probing payment methodology', status: 'adjusting', detail: 'DRG vs fee schedule analysis', timestamp: new Date(now.getTime() - 1000) },
      { step: 5, action: 'Confirming coordination of benefits', status: 'probing', detail: 'Primary payer status verified', timestamp: now },
    ],
    'Cigna': [
      { step: 1, action: 'Testing PT visit authorization', status: 'complete', detail: '15 visits approved, 12 remaining', timestamp: new Date(now.getTime() - 4000) },
      { step: 2, action: 'Validating medical necessity', status: 'complete', detail: 'Functional improvement documented', timestamp: new Date(now.getTime() - 3000) },
      { step: 3, action: 'Checking modifier requirements', status: 'success', detail: 'Modifier -25 for E/M same day', timestamp: new Date(now.getTime() - 2000) },
      { step: 4, action: 'Analyzing payment trends', status: 'adjusting', detail: 'Reimbursement stable at 76%', timestamp: new Date(now.getTime() - 1000) },
      { step: 5, action: 'Optimizing submission timing', status: 'probing', detail: 'Best window: Mon-Wed 6AM EST', timestamp: now },
    ],
    'Medicare': [
      { step: 1, action: 'Validating LCD compliance', status: 'complete', detail: 'L35041 criteria satisfied', timestamp: new Date(now.getTime() - 4000) },
      { step: 2, action: 'Testing ABN requirements', status: 'complete', detail: 'No ABN required - covered service', timestamp: new Date(now.getTime() - 3000) },
      { step: 3, action: 'Checking sequestration adjustment', status: 'success', detail: '2% reduction applied to estimate', timestamp: new Date(now.getTime() - 2000) },
      { step: 4, action: 'Validating MIPS compliance', status: 'adjusting', detail: 'Quality measures documented', timestamp: new Date(now.getTime() - 1000) },
      { step: 5, action: 'Confirming MAC processing', status: 'probing', detail: 'NGS processes within 14 days', timestamp: now },
    ],
    'Humana': [
      { step: 1, action: 'Checking MA plan benefits', status: 'complete', detail: 'HMO plan benefits verified', timestamp: new Date(now.getTime() - 4000) },
      { step: 2, action: 'Testing prior auth portal', status: 'complete', detail: 'Electronic auth supported', timestamp: new Date(now.getTime() - 3000) },
      { step: 3, action: 'Validating network status', status: 'success', detail: 'Tier 1 provider confirmed', timestamp: new Date(now.getTime() - 2000) },
      { step: 4, action: 'Analyzing payment timeline', status: 'adjusting', detail: 'Avg 22 days to payment', timestamp: new Date(now.getTime() - 1000) },
      { step: 5, action: 'Optimizing claim format', status: 'probing', detail: 'EDI 837P preferred', timestamp: now },
    ],
  };

  return payerSpecificTests[payer] || payerSpecificTests['Aetna'];
}

// Generate underpayment detection records
export function generateUnderpaymentRecords(): UnderpaymentRecord[] {
  const records: UnderpaymentRecord[] = [
    {
      id: 'UP-001',
      payer: 'UnitedHealthcare',
      claimId: 'CLM-2026-8847',
      cptCode: '27447',
      expectedRate: 18450.00,
      actualPaid: 16205.00,
      variance: 2245.00,
      variancePercent: 12.2,
      recoveryStatus: 'recovered',
      recoveryAmount: 2245.00,
    },
    {
      id: 'UP-002',
      payer: 'BCBS',
      claimId: 'CLM-2026-8912',
      cptCode: '29881',
      expectedRate: 4850.00,
      actualPaid: 4120.00,
      variance: 730.00,
      variancePercent: 15.1,
      recoveryStatus: 'auto-appealed',
      recoveryAmount: 0,
    },
    {
      id: 'UP-003',
      payer: 'Aetna',
      claimId: 'CLM-2026-9001',
      cptCode: '27130',
      expectedRate: 21200.00,
      actualPaid: 19880.00,
      variance: 1320.00,
      variancePercent: 6.2,
      recoveryStatus: 'recovered',
      recoveryAmount: 1320.00,
    },
    {
      id: 'UP-004',
      payer: 'Cigna',
      claimId: 'CLM-2026-9145',
      cptCode: '29827',
      expectedRate: 8900.00,
      actualPaid: 7650.00,
      variance: 1250.00,
      variancePercent: 14.0,
      recoveryStatus: 'pending',
      recoveryAmount: 0,
    },
    {
      id: 'UP-005',
      payer: 'Medicare',
      claimId: 'CLM-2026-9203',
      cptCode: '20610',
      expectedRate: 142.00,
      actualPaid: 118.00,
      variance: 24.00,
      variancePercent: 16.9,
      recoveryStatus: 'detected',
      recoveryAmount: 0,
    },
    {
      id: 'UP-006',
      payer: 'UnitedHealthcare',
      claimId: 'CLM-2026-9287',
      cptCode: '97110',
      expectedRate: 89.00,
      actualPaid: 71.00,
      variance: 18.00,
      variancePercent: 20.2,
      recoveryStatus: 'auto-appealed',
      recoveryAmount: 0,
    },
  ];

  return records;
}

// Calculate total recovery stats
export function calculateRecoveryStats(records: UnderpaymentRecord[]) {
  const totalVariance = records.reduce((sum, r) => sum + r.variance, 0);
  const totalRecovered = records.reduce((sum, r) => sum + r.recoveryAmount, 0);
  const pendingRecovery = totalVariance - totalRecovered;
  const autoAppealedCount = records.filter(r => r.recoveryStatus === 'auto-appealed').length;
  const recoveredCount = records.filter(r => r.recoveryStatus === 'recovered').length;

  return {
    totalVariance,
    totalRecovered,
    pendingRecovery,
    autoAppealedCount,
    recoveredCount,
    recoveryRate: totalRecovered / totalVariance * 100,
  };
}

// Simulate a payer update event
export function simulatePayerUpdate(currentData: PayerDriftData[]): PayerDriftData[] {
  return currentData.map((payer) => {
    // Small random drift changes
    const driftChange = Math.floor(Math.random() * 10) - 5;
    const newDriftScore = Math.min(100, Math.max(0, payer.driftScore + driftChange));
    
    return {
      ...payer,
      driftScore: newDriftScore,
      isHighVolatility: newDriftScore >= 75,
      lastUpdated: new Date(),
    };
  });
}
